import {
  OffsiteMessage,
  OffsiteMessageSegment,
  OnsiteMessage,
  OnsiteMessageSegment,
  SegmentWithScheduling,
  SendEmailMessage,
  Variant,
  WebsiteContentMessage,
} from 'sections/onsite-campaigns/types';
import { CampaignEditorState, CampaignMessage } from '../../../contexts/CampaignEditorState';
import { convertSegmentRuleValues } from '../utils/convertSegmentRuleValues';
import { Branches } from '../../../tabs/CampaignWorkflowTab/state/Branches/BranchesState';
import { CanvasState } from '../../../tabs/CampaignWorkflowTab/components/Canvas/state/CanvasState';
import { SplitTest } from '../../../tabs/CampaignWorkflowTab/state/SplitTests/SplitTestsState';
import { uuid } from 'utils';
import { SplitTestIdGenerator } from './SplitTestIdGenerator';
import { MapMessageSegmentsModel, MessageSegmentationMapperModel } from './MessageSegmentationMapperModel';
import { mapStateToTriggers } from '../utils/mapStateToTriggers';
import { setTimeFor } from '../utils/datetime';
import { FlowService } from '../../../domain/Services/FlowService';
import { NodeTypeKeys } from '../../../domain/Canvas/utils/NodeTypeKeys';

/** Weight defaulted to 100 to ensure a variant is always used. */
const ALWAYS_USE_THIS_VARIANT = 100;

/**
 * Tries to find the segment for a given branch. It will first try and match a segment by checking
 * if the segmentation ID and branch ID matches. If a match is not found, it will then try and match
 * a segment by comparing each variant ID and branch ID. If they match then the segment will be returned.
 *
 * @param segmentation
 * @param nodeId
 */
const findSegmentByBranchId = (segmentation: OnsiteMessageSegment[], branchId: string) => {
  for (const segment of segmentation) {
    if (segment.segmentId === branchId) {
      return segment;
    }

    for (const variant of segment.variants) {
      if (variant.id === branchId) {
        return segment;
      }
    }
  }
};

/**
 * Tries to assign a branch name to it's corresponding segment. If a segment can't be found then it's assumed
 * that it's an empty branch and an empty segment is created with the branch name.
 *
 * @param segmentation Segmentation to assign branch names to
 * @param branches Branches. Key of branch is either segment ID or random index value.
 */
const assignBranchNameToSegmentation = (segmentation: OnsiteMessageSegment[], branches: Branches) => {
  if (!branches || branches.length) {
    return;
  }

  Object.keys(branches).forEach(k => {
    const branch = branches[k];
    const segment = findSegmentByBranchId(segmentation, k);

    if (segment) {
      segment.branchName = branch.name;
    } else {
      segmentation.push({
        segmentationRules: undefined,
        segmentId: uuid(),
        controlGroupEnabled: undefined,
        controlGroupWeight: undefined,
        branchName: branch.name,
        offsiteMessageSegments: [],
        splitTestData: {
          id: undefined,
          name: undefined,
          splitTestEnabled: false,
          startDate: undefined,
          endDate: undefined,
        },
        variants: [],
      });
    }
  });

  const sortedBranches = Object.keys(branches)
    .map(k => branches[k])
    .sort((a, b) => a.priority - b.priority);

  segmentation = segmentation.sort(
    (a, b) =>
      sortedBranches.findIndex(s => s.name === a.branchName) - sortedBranches.findIndex(s => s.name === b.branchName),
  );
};

const createSplitTest = (
  prevSplitTest: SplitTest,
  prevTemplateIds: string[],
  splitTest: SplitTest,
  templateIds: string[],
) => {
  if (!splitTest) {
    return {
      id: undefined,
      name: undefined,
      splitTestEnabled: false,
      startDate: undefined,
      endDate: undefined,
    };
  }

  const startDate = splitTest.scheduling.startDate;
  if (startDate) {
    const hoursAndMinutes = splitTest.scheduling.startTime.split(':');

    setTimeFor(startDate, { hours: Number(hoursAndMinutes[0] ?? 0), minutes: Number(hoursAndMinutes[1] ?? 0) });
  }

  const endDate = splitTest.scheduling.endDate;
  if (endDate) {
    const endDateHoursAndMinutes = splitTest.scheduling.endTime.split(':');

    setTimeFor(endDate, {
      hours: Number(endDateHoursAndMinutes[0] ?? 0),
      minutes: Number(endDateHoursAndMinutes[1] ?? 0),
    });
  }

  return {
    /*
      If we can find a split test then it means that split test has been enabled. If one cannot be
      found then `splitTestEnabled` will be false and there will only be one variant created 
      for this segment, and it will be safe to use the default settings for the variant.
    */
    splitTestEnabled: !!splitTest,
    startDate: startDate ? startDate.getTime() : undefined,
    endDate: endDate ? endDate.getTime() : undefined,
    name: splitTest.name,
    id: SplitTestIdGenerator.generate(
      { ...prevSplitTest, variantTemplateIds: prevTemplateIds },
      { ...splitTest, variantTemplateIds: templateIds },
    ),
  };
};

export class MessageSegmentationMapper {
  private static getOnsiteAndOffsiteMessagesForSegmentMapping(
    model: MapMessageSegmentsModel,
    canvasState: CanvasState,
  ): {
    onsiteMessages: CampaignMessage<WebsiteContentMessage>[];
    offsiteMessages: CampaignMessage<SendEmailMessage>[];
  } {
    const onsiteMessages = model.onsiteMessages;
    let offsiteMessages = model.offsiteMessages;

    if (onsiteMessages.length <= 1 || Object.keys(model.branchNames).length > 0) {
      return { onsiteMessages, offsiteMessages };
    }

    const displayWebNodes = (canvasState.nodes ?? []).filter(n => n.type === NodeTypeKeys.displayWebContentNodeType);

    if (displayWebNodes.length !== 1) {
      return { onsiteMessages, offsiteMessages };
    }

    try {
      if (new FlowService(canvasState).isBranchingEnabled()) {
        return { onsiteMessages, offsiteMessages };
      }
    } catch {
      return { onsiteMessages, offsiteMessages };
    }

    const keepId = displayWebNodes[0].id;
    const keepMessage = onsiteMessages.find(m => m.id === keepId);
    if (!keepMessage) {
      return { onsiteMessages, offsiteMessages };
    }

    const removedSegmentIds = new Set(
      onsiteMessages
        .filter(m => m.id !== keepId)
        .map(m => m.segmentId)
        .filter((id): id is string => !!id),
    );

    offsiteMessages = offsiteMessages.map(m => {
      if (m.onsiteSegmentId && removedSegmentIds.has(m.onsiteSegmentId)) {
        return { ...m, onsiteSegmentId: keepMessage.segmentId };
      }
      return m;
    });

    return { onsiteMessages: [keepMessage], offsiteMessages };
  }

  private static mapOnsiteMessageSegments = (
    model: MapMessageSegmentsModel,
    canvasState: CanvasState,
  ): OnsiteMessageSegment[] => {
    if (!model.onsiteMessages.length && !model.offsiteMessages.length && !Object.keys(model.branchNames).length) {
      return;
    }

    const onlyOffsiteMessageAdded = !model.onsiteMessages.length && model.offsiteMessages.length;
    if (onlyOffsiteMessageAdded) {
      return [
        {
          segmentationRules: undefined,
          segmentId: undefined,
          branchName: undefined,
          splitTestData: {
            id: undefined,
            name: undefined,
            splitTestEnabled: undefined,
            startDate: undefined,
            endDate: undefined,
          },
          variants: undefined,
          controlGroupEnabled: undefined,
          controlGroupWeight: undefined,
          offsiteMessageSegments: [
            {
              segmentId: model.offsiteMessages[0].segmentId,
              splitTestData: {
                splitTestEnabled: undefined,
              },
              variants: [
                {
                  id: model.offsiteMessages[0].id,
                  name: model.offsiteMessages[0].name,
                  weight: undefined,
                  subjectLineId: model.offsiteMessages[0].subjectLine?.id,
                  messageTemplateId: model.offsiteMessages[0].template?.id,
                  isDefault: model.offsiteMessages[0].isDefault,
                },
              ],
            },
          ],
        },
      ];
    }

    /**
     We group each message by their corresponding segment ID. This allows us to map
     the message as a variant to the segment it belongs to.
     */

    const groupMessagesBySegmentId = (
      messages: CampaignMessage<WebsiteContentMessage>[],
    ): { [key: string]: CampaignMessage<WebsiteContentMessage>[] } => {
      return messages.reduce((prev, message) => {
        const targetSegmentId = message.segmentId;
        const targetSegment = prev[targetSegmentId] ?? [];

        return {
          ...prev,
          [targetSegmentId]: [...targetSegment, message],
        };
      }, {});
    };

    /**
     * We also group the offsite messages by onsite segment ID, and then by their offsite segment ID. This allows
     * us to easily grab the offsite segments for a particular onsite segment.
     */
    const { onsiteMessages, offsiteMessages } = MessageSegmentationMapper.getOnsiteAndOffsiteMessagesForSegmentMapping(
      model,
      canvasState,
    );

    const offsiteMessagesByOnsiteSegmentId: {
      [onsiteSegmentId: string]: {
        [offsiteSegmentId: string]: CampaignMessage<SendEmailMessage | SegmentWithScheduling>[];
      };
    } = offsiteMessages.reduce((prev, message) => {
      const onsiteSegmentId = message.onsiteSegmentId;
      const targetSegment = prev[onsiteSegmentId] ?? [];
      const bucket = targetSegment[message.segmentId] ?? [];

      return {
        ...prev,
        [onsiteSegmentId]: {
          ...targetSegment,
          [message.segmentId]: [...bucket, message],
        },
      };
    }, {});

    const messagesBySegmentId = groupMessagesBySegmentId(onsiteMessages);
    const prevMessagesBySegmentId = groupMessagesBySegmentId(model.prevOnsiteMessages);

    const segmentation: OnsiteMessageSegment[] = Object.keys(messagesBySegmentId).map((onsiteSegmentId, index) => {
      const onsiteMessages = messagesBySegmentId[onsiteSegmentId];
      const prevOnsiteMessages = prevMessagesBySegmentId[onsiteSegmentId];

      const offsiteSegments = offsiteMessagesByOnsiteSegmentId[onsiteSegmentId] ?? {};

      const splitTest = model.splitTests[onsiteSegmentId];
      const prevSplitTest = model.prevSplitTests[onsiteSegmentId];
      const multipleOnsiteVariants = onsiteMessages.length > 1;

      const scheduling = onsiteMessages[0].scheduling;
      let startDate: Date = undefined;
      let endDate: Date = undefined;
      if (scheduling?.startDate) {
        startDate = scheduling.startDate;
        const hoursAndMinutes = scheduling.startTime.split(':');
        setTimeFor(startDate, { hours: Number(hoursAndMinutes[0] ?? 0), minutes: Number(hoursAndMinutes[1] ?? 0) });
      }
      if (scheduling?.endDate) {
        endDate = scheduling.endDate;
        const hoursAndMinutes = scheduling.endTime.split(':');
        setTimeFor(endDate, { hours: Number(hoursAndMinutes[0] ?? 0), minutes: Number(hoursAndMinutes[1] ?? 0) });
      }

      return {
        startDate: startDate ? startDate.getTime() : undefined,
        endDate: endDate ? endDate.getTime() : undefined,
        segmentId: onsiteSegmentId,
        // Segment-level rules from the first message; split tests also set segmentationRules on each variant.
        segmentationRules: convertSegmentRuleValues(onsiteMessages[0].segmentationRules, true),
        /* 
          branchName and other branch-related properties are set later by `assignBranchNameToSegmentation`.
          This is because a given branch may not yet be associated to a segment. If this happens then
          a new segment needs to be created, otherwise a branch is mapped to its corresponding segment.
        */
        branchName: `Branch #${index + 1}`,
        controlGroupEnabled: !!splitTest ? splitTest.controlGroupEnabled : onsiteMessages[0].controlGroupEnabled,
        controlGroupWeight: !!splitTest ? splitTest.controlGroupWeight : onsiteMessages[0].controlGroupWeight,
        splitTestData: createSplitTest(
          prevSplitTest,
          prevOnsiteMessages?.map(s => s.template?.id).filter(s => s),
          splitTest,
          onsiteMessages?.map(s => s.template?.id).filter(s => s),
        ),
        variants: onsiteMessages.map<Variant<OnsiteMessage>>(onsiteMessage => {
          const variantInfo = splitTest?.variants.find(p => p.messageId === onsiteMessage.id);

          const mappedTriggers = mapStateToTriggers(onsiteMessage.triggers);
          const hasTriggers = mappedTriggers && Object.keys(mappedTriggers).length > 0;

          const variant: any = {
            id: onsiteMessage.id,
            templateId: onsiteMessage.template?.id,
            displayType: onsiteMessage.displayType,
            weight: variantInfo?.weight ?? ALWAYS_USE_THIS_VARIANT,
            name: variantInfo?.name ?? onsiteMessage.name,
            isDefault: variantInfo?.isDefault,
            selector: onsiteMessage.embeddedSelector,
            position: onsiteMessage.embeddedPosition,
          };

          if (hasTriggers) {
            variant.triggers = mappedTriggers;
          }

          if (multipleOnsiteVariants) {
            variant.segmentationRules = convertSegmentRuleValues(onsiteMessage.segmentationRules, true);
          }

          return variant as Variant<OnsiteMessage>;
        }),
        offsiteMessageSegments: Object.keys(offsiteSegments).map<OffsiteMessageSegment>(k => {
          const offsiteMessages = offsiteSegments[k];

          return {
            segmentId: k,
            splitTestData: {
              // An offsite message cannot yet be a split test.
              splitTestEnabled: false,
            },
            variants: offsiteMessages.map<Variant<OffsiteMessage>>(offsiteMessage => ({
              id: offsiteMessage.id,
              name: offsiteMessage.name,
              messageTemplateId: offsiteMessage.template?.id,
              subjectLineId: offsiteMessage.subjectLine?.id,
              // We can only have one offsite message at the moment, so we always want to use this variant.
              weight: ALWAYS_USE_THIS_VARIANT,
              isDefault: offsiteMessage?.isDefault,
            })),
          };
        }),
      };
    });

    assignBranchNameToSegmentation(segmentation, model.branchNames);

    return segmentation;
  };

  public static Map(
    prevState: CampaignEditorState,
    state: CampaignEditorState,
    canvasState: CanvasState,
  ): OnsiteMessageSegment[] {
    const model = MessageSegmentationMapperModel.map(prevState, state, canvasState);

    return MessageSegmentationMapper.mapOnsiteMessageSegments(model, canvasState);
  }
}
